import { isVideoFile } from "@/src/utils/attachment.utils";
import { ArrowLeft2, ArrowRight2 } from "iconsax-react-nativejs";
import React, { useRef, useState } from "react";
import {
  FlatList,
  Platform,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { useTheme } from "@/src/styles/theme-provider";
import VideoPlayer from "../videos/video-player";

type MediaCarouselProps = Readonly<{
  media: string[];
  initialIndex?: number;
}>;

const IMAGE_HEIGHT = 350;
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;

// Zoomable image that works on both mobile and web:
//  • pinch to zoom (mobile / trackpad)
//  • double-tap / double-click toggles between fit and a 2.5× zoom
//  • drag to pan once zoomed (finger on mobile, mouse on web)
// The zoom level persists (it does not spring back) and the pan stays clamped
// to the image bounds. Panning only claims the gesture while zoomed, so the
// surrounding carousel can still page between images at 1×.
const ZoomableImage = ({ uri, width }: { uri: string; width: number }) => {
  const scale = useSharedValue(1);
  const savedScale = useSharedValue(1);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const savedTranslateX = useSharedValue(0);
  const savedTranslateY = useSharedValue(0);

  const clamp = (value: number, max: number) => {
    "worklet";
    return Math.min(Math.max(value, -max), max);
  };

  const reset = () => {
    "worklet";
    scale.value = withTiming(MIN_SCALE, { duration: 180 });
    savedScale.value = MIN_SCALE;
    translateX.value = withTiming(0, { duration: 180 });
    translateY.value = withTiming(0, { duration: 180 });
    savedTranslateX.value = 0;
    savedTranslateY.value = 0;
  };

  const pinch = Gesture.Pinch()
    .onUpdate((event) => {
      scale.value = clamp(
        savedScale.value * event.scale,
        MAX_SCALE,
      );
      // clamp() is symmetric; force the lower bound for scale.
      if (scale.value < MIN_SCALE) scale.value = MIN_SCALE;
    })
    .onEnd(() => {
      if (scale.value <= MIN_SCALE) {
        reset();
      } else {
        savedScale.value = scale.value;
      }
    });

  const pan = Gesture.Pan()
    .averageTouches(true)
    .manualActivation(true)
    .onTouchesMove((_event, manager) => {
      // Only take over for panning once the image is zoomed in; otherwise let
      // the carousel handle the horizontal swipe.
      if (savedScale.value > MIN_SCALE) manager.activate();
      else manager.fail();
    })
    .onUpdate((event) => {
      const maxX = (width * (savedScale.value - 1)) / 2;
      const maxY = (IMAGE_HEIGHT * (savedScale.value - 1)) / 2;
      translateX.value = clamp(savedTranslateX.value + event.translationX, maxX);
      translateY.value = clamp(savedTranslateY.value + event.translationY, maxY);
    })
    .onEnd(() => {
      savedTranslateX.value = translateX.value;
      savedTranslateY.value = translateY.value;
    });

  const doubleTap = Gesture.Tap()
    .numberOfTaps(2)
    .maxDuration(300)
    .onEnd(() => {
      if (savedScale.value > MIN_SCALE) {
        reset();
      } else {
        scale.value = withTiming(DOUBLE_TAP_SCALE, { duration: 180 });
        savedScale.value = DOUBLE_TAP_SCALE;
      }
    });

  const gesture = Gesture.Exclusive(
    doubleTap,
    Gesture.Simultaneous(pinch, pan),
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
      { scale: scale.value },
    ],
  }));

  return (
    <GestureDetector gesture={gesture}>
      <Animated.Image
        source={{ uri }}
        style={[{ width: "100%", height: IMAGE_HEIGHT }, animatedStyle]}
        resizeMode="contain"
      />
    </GestureDetector>
  );
};

export const MediaCarousel = ({
  media,
  initialIndex = 0,
}: MediaCarouselProps) => {
  const { themeVars } = useTheme();
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const flatListRef = useRef<FlatList>(null);

  const goPrev = () => {
    if (currentIndex > 0) {
      flatListRef.current?.scrollToIndex({
        index: currentIndex - 1,
        animated: true,
      });
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goNext = () => {
    if (currentIndex < media.length - 1) {
      flatListRef.current?.scrollToIndex({
        index: currentIndex + 1,
        animated: true,
      });
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <View
      className="relative"
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
    >
      {containerWidth > 0 && (
        <FlatList
          ref={flatListRef}
          data={media}
          horizontal
          pagingEnabled
          scrollEnabled={Platform.OS !== "web"}
          showsHorizontalScrollIndicator={false}
          keyExtractor={(item, i) => item.url ?? i.toString()}
          initialScrollIndex={initialIndex}
          onMomentumScrollEnd={(e) => {
            const index = Math.round(
              e.nativeEvent.contentOffset.x / containerWidth,
            );
            setCurrentIndex(index);
          }}
          getItemLayout={(_, index) => ({
            length: containerWidth,
            offset: containerWidth * index,
            index,
          })}
          renderItem={({ item }) => (
            <View
              style={{
                width: containerWidth,
                backgroundColor: themeVars["--color-ash-default"] as string,
              }}
            >
              {isVideoFile(item) ? (
                <VideoPlayer uri={item} width={containerWidth} height={300} />
              ) : (
                <ZoomableImage uri={item} width={containerWidth} />
              )}
            </View>
          )}
        />
      )}

      {Platform.OS === "web" && currentIndex > 0 && (
        <TouchableOpacity
          onPress={goPrev}
          className="absolute top-1/2 left-2 z-10 p-2 bg-black/30 rounded-full -translate-y-1/2 hover:bg-ash-3"
        >
          <ArrowLeft2 size={24} color="white" />
        </TouchableOpacity>
      )}

      {Platform.OS === "web" && currentIndex < media.length - 1 && (
        <TouchableOpacity
          onPress={goNext}
          className="absolute top-1/2 right-2 z-10 p-2 bg-black/30 rounded-full -translate-y-1/2 hover:bg-ash-3"
        >
          <ArrowRight2 size={24} color="white" />
        </TouchableOpacity>
      )}

      {media.length > 1 && (
        <View className="absolute top-4 right-4 bg-primary px-3 py-1 rounded-full">
          <Text className="text-white text-xs">
            {currentIndex + 1}/{media.length}
          </Text>
        </View>
      )}
    </View>
  );
};
